<script setup lang="ts">
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChartNoAxesColumnIncreasing, Check, Circle, Clock3, ListTodo, LoaderCircle, MessageCircleReply, ShieldCheck, TriangleAlert } from 'lucide-vue-next';
import {
    calculateProjectMilestoneOperationalStatus,
    type ProjectMilestoneOperationalStatus,
    type ProjectMilestoneState,
} from '~/lib/projectMilestones';
import type { Approval, ProjectStage, Task } from '~/types/models';

const props = withDefaults(defineProps<{
    stages: ProjectStage[];
    tasks: Task[];
    approvals?: Approval[];
    currentStage?: string;
    audience?: 'staff' | 'portal';
    showTaskCounts?: boolean;
    compact?: boolean;
    ariaLabel?: string;
}>(), {
    currentStage: '',
    approvals: () => [],
    audience: 'staff',
    showTaskCounts: undefined,
    compact: false,
    ariaLabel: 'Project milestone progress',
});

const milestones = computed(() => calculateProjectMilestoneOperationalStatus(
    props.stages,
    props.tasks,
    props.currentStage,
    props.approvals,
));
const shouldShowTaskCounts = computed(() => props.showTaskCounts ?? true);
const openTooltipId = ref<string | null>(null);
const segmentStyle = computed(() => ({
    gridTemplateColumns: `repeat(${Math.max(1, milestones.value.length)}, minmax(0, 1fr))`,
}));

function segmentClipPath(index: number): string {
    const left = index === 0 ? '0 0, 0 100%' : '0.125rem 0, 0 100%';
    const right = index === milestones.value.length - 1 ? '100% 100%, 100% 0' : 'calc(100% - 0.125rem) 100%, 100% 0';
    return `polygon(${left}, ${right})`;
}

function segmentCornerStyle(index: number) {
    const radius = 'var(--radius)';
    return {
        clipPath: segmentClipPath(index),
        borderTopLeftRadius: index === 0 ? radius : undefined,
        borderBottomLeftRadius: index === 0 ? radius : undefined,
        borderTopRightRadius: index === milestones.value.length - 1 ? radius : undefined,
        borderBottomRightRadius: index === milestones.value.length - 1 ? radius : undefined,
    };
}

function milestoneStateLabel(state: ProjectMilestoneState): string {
    if (state === 'completed') return 'Completed';
    if (state === 'approval_required') return 'Approval required';
    if (state === 'in_progress') return 'In progress';
    return 'Not reached';
}

function milestoneAttentionTone(milestone: ProjectMilestoneOperationalStatus): 'danger' | 'waiting' | 'reply' | 'normal' {
    if (milestone.overdueTaskCount || milestone.approval?.status === 'rejected') return 'danger';
    if (milestone.state === 'approval_required' || milestone.waitingOnClientCount) return 'waiting';
    if (milestone.waitingOnTeamCount) return 'reply';
    return 'normal';
}

type MilestoneTone = ReturnType<typeof milestoneAttentionTone>;

function milestoneToneColor(tone: MilestoneTone): string {
    const themeAccent = 'light-dark(var(--brand-accent), var(--brand-dark-accent))';
    if (tone === 'danger') return `color-mix(in srgb, var(--destructive) 68%, ${themeAccent})`;
    if (tone === 'waiting') return `color-mix(in srgb, ${themeAccent} 72%, var(--foreground))`;
    if (tone === 'reply') return 'color-mix(in srgb, var(--primary) 78%, var(--foreground))';
    return 'var(--primary)';
}

function milestoneFillColor(milestone: ProjectMilestoneOperationalStatus): string {
    return milestoneToneColor(milestoneAttentionTone(milestone));
}

function milestoneToneStyle(tone: MilestoneTone) {
    const color = milestoneToneColor(tone);
    return {
        color: `color-mix(in srgb, ${color} 78%, var(--foreground))`,
        backgroundColor: `color-mix(in srgb, ${color} 12%, var(--popover))`,
    };
}

function milestoneTextClass(milestone: ProjectMilestoneOperationalStatus): string {
    if (milestoneAttentionTone(milestone) !== 'normal') return '';
    return milestone.isCurrent ? 'text-primary' : 'text-muted-foreground';
}

function milestoneTextStyle(milestone: ProjectMilestoneOperationalStatus) {
    return milestoneAttentionTone(milestone) === 'normal'
        ? undefined
        : { color: `color-mix(in srgb, ${milestoneFillColor(milestone)} 78%, var(--foreground))` };
}

function milestoneTooltipNotes(milestone: ProjectMilestoneOperationalStatus): { text: string; tone: MilestoneTone }[] {
    const notes: { text: string; tone: MilestoneTone }[] = [];
    if (milestone.overdueTaskCount && !milestone.statusLabel.includes('overdue')) {
        notes.push({ text: `${milestone.overdueTaskCount} task${milestone.overdueTaskCount === 1 ? '' : 's'} overdue`, tone: 'danger' });
    }
    if (milestone.waitingOnClientCount && milestone.statusLabel !== 'Waiting on client') {
        notes.push({ text: 'Waiting on client', tone: 'waiting' });
    }
    if (milestone.requiresClientApproval && !milestone.isComplete
        && !['Awaiting approval', 'Changes requested', 'Approval required'].includes(milestone.statusLabel)) {
        notes.push({ text: 'Client approval required before completion', tone: 'waiting' });
    }
    return notes;
}

function milestoneTooltipIcon(milestone: ProjectMilestoneOperationalStatus) {
    const tone = milestoneAttentionTone(milestone);
    if (tone === 'danger') return TriangleAlert;
    if (tone === 'waiting') return milestone.state === 'approval_required' ? ShieldCheck : Clock3;
    if (tone === 'reply') return MessageCircleReply;
    if (milestone.state === 'completed') return Check;
    if (milestone.state === 'in_progress') return LoaderCircle;
    return Circle;
}

function setTooltipOpen(id: string, open: boolean): void {
    openTooltipId.value = open ? id : openTooltipId.value === id ? null : openTooltipId.value;
}
</script>

<template>
    <section v-if="milestones.length" class="min-w-0" :aria-label="ariaLabel">
        <TooltipProvider>
        <ol class="grid min-w-0 gap-x-0.5" :style="segmentStyle">
            <li v-for="(milestone, index) in milestones" :key="milestone.id" class="min-w-0">
                <Tooltip :open="openTooltipId === milestone.id" @update:open="setTooltipOpen(milestone.id, $event)">
                    <TooltipTrigger as-child>
                        <button
                            type="button"
                            :aria-label="`${milestone.name}: ${milestone.statusLabel}. ${milestone.statusDetail}`"
                            class="block w-full min-w-0 rounded-sm text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                            @click.stop="setTooltipOpen(milestone.id, true)"
                        >
                            <div
                                v-if="!compact"
                                class="mb-1.5 flex min-w-0 items-center gap-1.5 pl-1 text-xs text-muted-foreground"
                                :style="milestoneTextStyle(milestone)"
                            >
                                <Check v-if="milestone.state === 'completed'" class="size-3 shrink-0" :class="milestoneTextClass(milestone)" aria-hidden="true" />
                                <ShieldCheck v-else-if="milestone.state === 'approval_required'" class="size-3 shrink-0" :class="milestoneTextClass(milestone)" aria-hidden="true" />
                                <LoaderCircle v-else-if="milestone.state === 'in_progress'" class="size-3 shrink-0" :class="milestoneTextClass(milestone)" aria-hidden="true" />
                                <Circle v-else class="size-3 shrink-0" :class="milestoneTextClass(milestone)" aria-hidden="true" />
                                <span class="truncate font-medium" :class="milestoneTextClass(milestone)">{{ milestone.name }}</span>
                            </div>

                            <div class="overflow-hidden" :style="segmentCornerStyle(index)">
                                <Progress
                                    :model-value="Math.max(0, Math.min(100, milestone.progress))"
                                    :indicator-color="milestoneFillColor(milestone)"
                                    :track-color="`color-mix(in srgb, ${milestoneFillColor(milestone)} 32%, var(--muted))`"
                                    class="w-full rounded-none milestone-segment-progress"
                                    :class="compact ? 'h-2' : 'h-3'"
                                    :aria-label="`${milestone.name}: ${milestoneStateLabel(milestone.state)}, ${milestone.progress}% complete`"
                                />
                            </div>

                        </button>
                    </TooltipTrigger>
                    <TooltipContent
                        side="top"
                        class="min-w-52 max-w-72 border border-border !bg-popover p-3 text-left !text-popover-foreground shadow-lg [&>svg]:!bg-popover [&>svg]:!fill-popover"
                    >
                        <div class="flex items-start gap-2.5">
                            <span class="flex size-7 shrink-0 items-center justify-center rounded-md" :style="milestoneToneStyle(milestoneAttentionTone(milestone))">
                                <component :is="milestoneTooltipIcon(milestone)" class="size-4" aria-hidden="true" />
                            </span>
                            <p class="min-w-0 pt-1 text-sm font-semibold leading-5 text-popover-foreground">{{ milestone.name }}</p>
                        </div>
                        <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span v-if="shouldShowTaskCounts" class="inline-flex items-center gap-1">
                                <ListTodo class="size-3.5" aria-hidden="true" />
                                {{ milestone.taskCount ? `${milestone.completedTasks} of ${milestone.taskCount} tasks` : 'No tasks' }}
                            </span>
                            <span v-if="milestone.taskCount || milestone.isComplete" class="inline-flex items-center gap-1">
                                <ChartNoAxesColumnIncreasing class="size-3.5" aria-hidden="true" />
                                {{ milestone.progress }}% complete
                            </span>
                        </div>
                        <div class="mt-2 rounded-md px-2.5 py-1.5 text-xs font-medium" :style="milestoneToneStyle(milestoneAttentionTone(milestone))">
                            {{ milestone.statusLabel }}
                        </div>
                        <div v-if="milestoneTooltipNotes(milestone).length" class="mt-2 space-y-1">
                            <p
                                v-for="note in milestoneTooltipNotes(milestone)"
                                :key="note.text"
                                class="flex items-center gap-1.5 text-xs font-medium"
                                :style="{ color: milestoneToneStyle(note.tone).color }"
                            >
                                <TriangleAlert v-if="note.tone === 'danger'" class="size-3.5 shrink-0" aria-hidden="true" />
                                <Clock3 v-else class="size-3.5 shrink-0" aria-hidden="true" />
                                {{ note.text }}
                            </p>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </li>
        </ol>
        </TooltipProvider>
    </section>
</template>

<style scoped>
@media (prefers-reduced-motion: reduce) {
    .milestone-segment-progress :deep([data-slot='progress-indicator']) { transition: none; }
}
</style>
